/**
 * Sentry Feedback 工具函數
 * 
 * 用於打開 Sentry User Feedback widget
 */

/**
 * 打開 Sentry Feedback widget
 * @param {object} options - 可選配置
 * @param {string} options.title - 反饋標題
 * @param {string} options.labelName - 名稱標籤
 * @param {string} options.labelEmail - 郵箱標籤
 * @param {string} options.labelComments - 評論標籤
 * @param {string} options.labelSubmit - 提交按鈕標籤
 * @param {string} options.labelCancel - 取消按鈕標籤
 * @param {string} options.successMessage - 成功消息
 * @param {string} options.language - 語言 ('tc' 或 'en')
 */
export const openSentryFeedback = async (options = {}) => {
  const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
  const language = options.language || 'tc';
  
  // 需要 Sentry DSN 才能使用
  if (!SENTRY_DSN) {
    console.warn('Sentry feedback requires REACT_APP_SENTRY_DSN to be configured');
    const errorMessage = language === 'tc' 
      ? '反饋功能需要配置 Sentry。請聯繫開發團隊。'
      : 'Feedback feature requires Sentry configuration. Please contact the development team.';
    alert(errorMessage);
    return;
  }

  try {
    const Sentry = await import('@sentry/react');
    
    // 檢查 Sentry 是否已初始化
    const client = Sentry.getClient();
    if (!client) {
      console.warn('Sentry is not initialized');
      return;
    }

    // 準備自定義標籤配置
    const feedbackOptions = {
      title: options.title,
      labelName: options.labelName,
      labelEmail: options.labelEmail,
      labelComments: options.labelComments,
      labelSubmit: options.labelSubmit,
      labelCancel: options.labelCancel,
      successMessage: options.successMessage,
      // 確保使用中文語言環境
      locale: language === 'tc' ? 'zh-TW' : 'en',
    };

    // 方法 1: 嘗試使用 getFeedback() API (Sentry v10+)
    // 這是推薦的方式
    if (typeof Sentry.getFeedback === 'function') {
      try {
        const feedback = Sentry.getFeedback();
        if (feedback) {
          const form = await feedback?.createForm(feedbackOptions);
          if (form) {
            form?.appendToDom();
            form?.open();
            return;
          }
        }
      } catch (err) {
        console.warn('Failed to use getFeedback API:', err);
        // 繼續嘗試其他方法
      }
    }

    // 方法 2: 嘗試通過 integration 獲取並使用 openDialog
    const feedbackIntegration = client.getIntegrationByName('Feedback');
    if (feedbackIntegration) {
      // 檢查是否有 openDialog 方法
      if (typeof feedbackIntegration.openDialog === 'function') {
        feedbackIntegration.openDialog(feedbackOptions);
        return;
      }
      // 檢查是否有 open 方法
      if (typeof feedbackIntegration.open === 'function') {
        feedbackIntegration.open(feedbackOptions);
        return;
      }
    }

    // 方法 3: 嘗試使用舊版 showReportDialog API
    if (typeof Sentry.showReportDialog === 'function') {
      Sentry.showReportDialog(feedbackOptions);
      return;
    }

    // 如果所有方法都失敗，顯示警告
    console.warn('Sentry feedback dialog method not available. Client:', client, 'Integration:', feedbackIntegration);
    console.warn('Available Sentry methods:', Object.keys(Sentry).filter(key => typeof Sentry[key] === 'function'));
    alert('無法打開反饋表單，請檢查 Sentry 配置。');
  } catch (err) {
    console.error('Failed to open Sentry feedback:', err);
    alert('打開反饋表單時發生錯誤，請稍後再試。');
  }
};

/**
 * 檢查 Sentry Feedback 是否可用
 * @returns {Promise<boolean>}
 */
export const isSentryFeedbackAvailable = async () => {
  const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
  
  if (!SENTRY_DSN) {
    return false;
  }

  try {
    const Sentry = await import('@sentry/react');
    
    // 檢查是否有 getFeedback 方法
    if (typeof Sentry.getFeedback === 'function') {
      const feedback = Sentry.getFeedback();
      if (feedback) {
        return true;
      }
    }

    const client = Sentry.getClient();
    if (!client) {
      return false;
    }

    const feedbackIntegration = client.getIntegrationByName('Feedback');
    return !!feedbackIntegration;
  } catch (err) {
    return false;
  }
};

